import React, { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import { profileRepository } from '../repositories/supabaseProfileRepository';
import { useAuth } from '@/contexts/AuthContext';
import { PostCard } from '@/components/social/PostCard';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import ProfilePhotoUpload from './ProfilePhotoUpload';
import { ConnectionButton } from '@/components/connections/ConnectionButton';
import { MessageButton } from '@/components/messages/MessageButton';
import { useConnectionStatus } from '@/shared/hooks/useConnectionStatus';
import { useCanMessage } from '@/shared/hooks/useCanMessage';
import { useMyConnections } from '@/shared/hooks/useMyConnections';
import { useUserConnections } from '@/shared/hooks/useUserConnections';
import { InstructorsSection } from './InstructorsSection';
import { ProfileTotalsSlider } from './ProfileTotalsSlider';
import { MyJourneyTab } from './MyJourneyTab';
import { useNavigate } from 'react-router-dom';
import { useProfileInstructors } from '@/features/profile/hooks/useProfileInstructors';
import { useI18n } from '@/contexts/I18nContext';
import { InstructorSlotDashboard } from './InstructorSlotDashboard';
import { useTechniques, getTechniqueName, getCategoryName } from '@/shared/hooks/useTechniques';
import { useUserAgendaConfig } from '@/features/calendar/hooks/useUserAgendaConfig';
import { useInstructorScheduleCounts } from '@/features/calendar/hooks/useInstructorSchedule';
import { AuthPrompt } from '@/components/ui/AuthPrompt';
import { PremiumBadge } from '@/components/ui/PremiumBadge';
import { getInitials } from '@/utils/avatarUtils';

// Simplified prop types for the skeleton
interface ProfileHighlightsProps {
  fullName: string;
  username: string;
  belt: string;
  beltColor: string;
  academies: { id: number; name: string }[];
  location: { country: string; state?: string; city?: string };
  social: { instagram?: string; x?: string; facebook?: string; youtube?: string };
  website?: string;
  interests: string[];
  profession?: string;
  favoriteTechniques: Record<string, string[]>;
  techniqueCategories: string[];
  businessType?: string;
  linkedin?: string;
  editable?: boolean;
  email?: string;
  birthday?: string;
  profilePictureUrl?: string | null;
  onProfileUpdated?: () => void | Promise<void>;
  isOwnProfile?: boolean;
  userId?: string;
  isInstructor?: boolean;
  isPremium?: boolean;
  /** Called when the user taps an empty-state CTA in their own profile */
  onOpenWizard?: (type: 'academy' | 'profession' | 'interests' | 'techniques' | 'social' | 'location') => void;
}

const ProfileHighlights: React.FC<ProfileHighlightsProps> = (props) => {
  const {
    fullName,
    username,
    belt,
    beltColor,
    academies,
    location,
    social,
    website,
    interests,
    profession,
    favoriteTechniques,
    techniqueCategories,
    businessType,
    linkedin,
    editable = false,
    email,
    birthday,
    profilePictureUrl: initialProfilePictureUrl,
    onProfileUpdated,
    isOwnProfile = false,
    userId,
    isInstructor = false,
    isPremium = false,
    onOpenWizard,
  } = props;

  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(initialProfilePictureUrl || null);
  
  // Buscar dados completos das técnicas do banco de dados
  const { data: techniqueCategoriesFromDB, isLoading: techniquesLoading } = useTechniques();

  // Função para obter o nome de exibição da categoria
  const getCategoryDisplayName = (category: string) => {
    // Primeiro tenta encontrar a categoria no banco de dados
    if (techniqueCategoriesFromDB && Array.isArray(techniqueCategoriesFromDB) && techniqueCategoriesFromDB.length > 0) {
      const foundCategory = techniqueCategoriesFromDB.find((cat: any) => cat.name === category);
      if (foundCategory) {
        return getCategoryName(foundCategory as any, currentLanguage);
      }
    }
    
    // Fallback para as traduções antigas
    switch (category) {
      case 'Guard Types': return t('onboarding.techniques.categories.guard_types');
      case 'Guard Passing': return t('onboarding.techniques.categories.guard_passing');
      case 'Submissions': return t('onboarding.techniques.categories.submissions');
      case 'Sweeps & Takedowns': return t('onboarding.techniques.categories.sweeps_takedowns');
      case 'Escapes & Reversals': return t('onboarding.techniques.categories.escapes_reversals');
      // Novas categorias do banco de dados
      case 'Guards': return t('calendar.technique_category_names.Guards') || 'Guards';
      case 'Guard Passes': return t('calendar.technique_category_names.Guard Passes') || 'Guard Passes';
      case 'Takedowns': return t('calendar.technique_category_names.Takedowns') || 'Takedowns';
      case 'Defense & Escapes': return t('calendar.technique_category_names.Defense & Escapes') || 'Defense & Escapes';
      default: return category;
    }
  };

  // Função para obter o nome de exibição da técnica
  const getTechniqueDisplayName = (techniqueName: string) => {
    if (techniqueCategoriesFromDB && Array.isArray(techniqueCategoriesFromDB) && techniqueCategoriesFromDB.length > 0) {
      // Procura a técnica em todas as categorias do banco de dados
      for (const category of techniqueCategoriesFromDB) {
        const foundTechnique = category.techniques.find((tech: any) => tech.name === techniqueName);
        if (foundTechnique) {
          return getTechniqueName(foundTechnique, currentLanguage);
        }
      }
    }
    // Fallback para o nome original se não encontrar
    return techniqueName;
  };

  const initials = getInitials(fullName, 'U');
  const { t, currentLanguage } = useI18n();
  
  // Agenda config — only needed for is_public check
  const { config: agendaConfig, isLoading: agendaLoading } = useUserAgendaConfig(userId);

  // Slot counts from RPC (same source of truth as InstructorSlots page)
  const scheduleCounts = useInstructorScheduleCounts(isInstructor ? userId : undefined, 14);
  const totalSlots     = scheduleCounts.reduce((sum, d) => sum + d.slotCount, 0);
  const slotsLoading   = agendaLoading || scheduleCounts.some(d => d.isLoading);

  // Location logic: if Brazil/USA, city | state - country; otherwise, just country
  let loc = '';
  let mainAcademy = '';
  
  if (location && location.country) {
    const countryLower = location.country.toLowerCase();
    
    if (countryLower === 'brazil' || countryLower === 'usa') {
      const city = location.city;
      const state = location.state ? location.state.toUpperCase() : undefined;
      
      if (city && state) {
        loc = `${city} | ${state} - ${location.country.charAt(0).toUpperCase() + location.country.slice(1).toLowerCase()}`;
      } else if (city) {
        loc = `${city} - ${location.country.charAt(0).toUpperCase() + location.country.slice(1).toLowerCase()}`;
      } else if (state) {
        const city = location.city;
        const state = location.state ? location.state.toUpperCase() : undefined;
        
        if (city && state) {
          loc = `${city} | ${state} - ${location.country.charAt(0).toUpperCase() + location.country.slice(1).toLowerCase()}`;
        } else if (city) {
          loc = `${city} - ${location.country.charAt(0).toUpperCase() + location.country.slice(1).toLowerCase()}`;
        } else if (state) {
          loc = `${state} - ${location.country.charAt(0).toUpperCase() + location.country.slice(1).toLowerCase()}`;
        } else {
          loc = location.country.charAt(0).toUpperCase() + location.country.slice(1).toLowerCase();
        }
      } else {
        loc = location.country.charAt(0).toUpperCase() + location.country.slice(1).toLowerCase();
      }
    } else {
      loc = location.country.charAt(0).toUpperCase() + location.country.slice(1).toLowerCase();
    }
  }

  // Academy logic
  if (academies && academies.length > 0) {
    mainAcademy = academies[0].name;
  } else if (editable && isOwnProfile) {
    // Only show "Add your Jiu-Jitsu school" for own profile when editable
    mainAcademy = t('profile.add_your_jiu_jitsu_school');
  } else {
    // For other users without academy, show nothing
    mainAcademy = '';
  }
  const hasAcademy = !!academies[0];

  // Check if any category has techniques
  const hasAnyTechniques = techniqueCategories.some(cat => {
    const techniques = (favoriteTechniques && favoriteTechniques[cat]) ? favoriteTechniques[cat] : [];
    return techniques.length > 0;
  });

  const [activeTab, setActiveTab] = useState<'profile' | 'social' | 'instructors'>('profile');
  const [activeTechCategory, setActiveTechCategory] = useState<string>('');

  // Resolve which technique category tab is active (defaults to first with techniques)
  const techCategoriesWithData = Object.entries(favoriteTechniques)
    .filter(([, techs]) => techs && techs.length > 0)
    .map(([cat]) => cat);
  const resolvedTechCategory =
    activeTechCategory && favoriteTechniques[activeTechCategory]?.length > 0
      ? activeTechCategory
      : techCategoriesWithData[0] || '';
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [authPromptMessage, setAuthPromptMessage] = useState<string | undefined>();

  const requireAuth = (message?: string) => {
    if (!user) {
      setAuthPromptMessage(message);
      setShowAuthPrompt(true);
      return true;
    }
    return false;
  };
  const { user } = useAuth();
  const [posts, setPosts] = useState<any[]>([]);
  const navigate = useNavigate();
  const [loadingPosts, setLoadingPosts] = useState(false);
  const { data: connectionsData, refetch } = useMyConnections();
  const connections = connectionsData?.pages?.flat() || [];
  const { connections: userConnections } = useUserConnections(userId);
  const { instructors } = useProfileInstructors(userId || '');
  const { status: connectionStatus } = useConnectionStatus(userId || '');
  const { canMessage } = useCanMessage(userId || '');
    
  // Check if user has instructors
  // Para o próprio usuário: sempre mostrar (mesmo sem instrutores)
  // Para outros usuários: mostrar apenas se tiverem instrutores
  const hasInstructors = isOwnProfile || (instructors && instructors.length > 0);

  const isEntrepreneur = (profession || '').trim().toLowerCase() === 'entrepreneur / business owner';

  // Utility function to build social links
  function getSocialLink(type: string, value?: string) {
    if (!value) return undefined;
    if (value.startsWith('http')) return value;
    switch (type) {
      case 'instagram':
        return `https://instagram.com/${value.replace(/^@/, '')}`;
      case 'x':
        return `https://x.com/${value.replace(/^@/, '')}`;
      case 'facebook':
        return `https://facebook.com/${value.replace(/^@/, '')}`;
      case 'youtube':
        return `https://youtube.com/@${value.replace(/^@/, '')}`;
      default:
        return value;
    }
  }


  useEffect(() => {
    const targetUserId = isOwnProfile ? user?.id : props.userId;
    if (!targetUserId) return;
    const fetchPosts = async () => {
      setLoadingPosts(true);
      const postData = await profileRepository.getPostsByUser(targetUserId);
      setPosts(
        postData.map((post) => ({
          id: post.id,
          content: post.content,
          created_at: post.created_at,
          user_id: post.user_id,
          visibility: post.visibility,
          author: {
            full_name: post.profiles?.full_name || '',
            belt_rank: post.profiles?.belt_rank || 'white',
            profile_picture_url: post.profiles?.profile_picture_url || null,
            username: post.profiles?.username || null,
          },
        }))
      );
      setLoadingPosts(false);
    };
    fetchPosts();
  }, [isOwnProfile, props.userId, user?.id]);

  return (
    <>
      <div className="rounded-xl py-3 mt-2 mb-4 relative shadow-md overflow-x-hidden">
        {/* Personal data + edit icon aligned */}
        <div className="flex flex-row gap-2 mb-4 items-start w-full">
          <div className="flex gap-4 flex-1 min-w-0">
            <div className="relative w-[60px] h-[66px] flex-shrink-0 mt-0">
              <FramedAvatar
                src={profilePictureUrl || undefined}
                alt={fullName}
                initials={initials}
                frameSize={{ w: 60, h: 66 }}
                showFrame={true}
                beltRank={belt}
              />
              
              {/* Upload/Remove buttons for own profile */}
              {editable && isOwnProfile && (
                <ProfilePhotoUpload
                  currentPhotoUrl={profilePictureUrl}
                  onPhotoUpdate={(url) => {
                    setProfilePictureUrl(url);
                    onProfileUpdated?.();
                  }}
                  onError={(error) => console.error('Photo upload error:', error)}
                  size="sm"
                  className="absolute inset-0"
                />
              )}
            </div>
            <div className="flex flex-col justify-start min-w-0 flex-1">
              {/* Name and username on the same line */}
              <div className="flex flex-col min-w-0">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-lg font-bold text-white leading-tight truncate font-roboto" style={{textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap'}}>{fullName || 'No Name'}</span>
                  {isPremium && <PremiumBadge size="xs" />}
                </div>
                {username ? (
                  <span className="text-xs text-white/70 font-roboto break-all font-normal">@{username}</span>
                ) : (
                  <span className="text-white/40 text-xs font-roboto italic font-normal">No username specified yet.</span>
                )}
              </div>
              {/* Main academy */}
              <div className="flex items-center gap-[4px] mt-1">
                {mainAcademy ? (
                  <span className="text-xs text-white/60 font-roboto font-normal truncate">{mainAcademy}</span>
                ) : null}
                {academies.length > 1 && (
                  <span className="ml-0.5 text-xs font-roboto text-white/60 border border-white/60 rounded-full flex items-center justify-center flex-shrink-0" style={{width: 16, height: 16, minWidth: 16, minHeight: 16, lineHeight: '16px', padding: 0}}>
                    +{academies.length - 1}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Profile Totals */}
        <ProfileTotalsSlider
          userId={userId || ''}
          isOwnProfile={isOwnProfile}
          onAuthRequired={() => requireAuth(t('auth.auth_prompt.explore_profile'))}
        />

        {/* Instructor Slot Dashboard - Show based on user type and privacy settings */}
        {userId && !slotsLoading && (() => {
          
          // Rule 1: Praticante - never show
          if (!isInstructor) return false;
          
          // Rule 2: Own profile - show if instructor (regardless of privacy)
          if (isOwnProfile) return true;
          
          // Rule 3: Other profiles - show only if instructor + public agenda
          const shouldShow = agendaConfig?.is_public === true;
          return shouldShow;
        })() && (
          <>
            <InstructorSlotDashboard
              instructorId={userId}
              instructorUsername={username}
              availableSlots={isOwnProfile ? totalSlots : (agendaConfig?.is_public ? totalSlots : 0)}
              isOwnProfile={isOwnProfile}
            />
          </>
        )}

        {/* Connection + Message buttons for other users - above tabs */}
        {!isOwnProfile && userId && (
          <div className="mb-4 mt-6 flex gap-2">
            {!user ? (
              /* Unauthenticated: show a single "Connect" button that triggers Auth Prompt */
              <div className="w-full">
                <button
                  onClick={() => requireAuth(t('auth.auth_prompt.connect_athlete'))}
                  className="w-full h-10 flex items-center justify-center rounded-full text-sm font-semibold border border-white/20 text-white/80 hover:text-white hover:border-white/40 transition-colors"
                >
                  Connect
                </button>
              </div>
            ) : (
              <>
                <div className={canMessage ? 'flex-1' : 'w-full'}>
                  <ConnectionButton targetUserId={userId} />
                </div>
                {canMessage && (
                  <div className="flex-1">
                    <MessageButton targetUserId={userId} />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mt-6 mb-2 border-b border-sossiety-overlay w-full">
          <button onClick={() => setActiveTab('profile')} className={`flex-1 pb-2 px-0 text-sm font-semibold whitespace-nowrap min-w-0 ${activeTab === 'profile' ? 'text-white border-b-2 border-sossiety-red' : 'text-white/60 border-b-2 border-transparent'}`}>{t('profile.about_me')}</button>
          <button onClick={() => setActiveTab('social')} className={`flex-1 pb-2 px-0 text-sm font-semibold whitespace-nowrap min-w-0 ${activeTab === 'social' ? 'text-white border-b-2 border-sossiety-red' : 'text-white/60 border-b-2 border-transparent'}`}>{t('profile.journey')}</button>
          {hasInstructors && (
            <button onClick={() => setActiveTab('instructors')} className={`flex-1 pb-2 px-0 text-sm font-semibold whitespace-nowrap min-w-0 ${activeTab === 'instructors' ? 'text-white border-b-2 border-sossiety-red' : 'text-white/60 border-b-2 border-transparent'}`}>{t('profile.instructor')}</button>
          )}
        </div>
        {/* Tab content */}
        {activeTab === 'profile' && (
          <div>
            {/* Location + Profession rows (always show, even if empty) */}
            <div className="divide-y divide-white/10">
              <div className="flex items-center justify-between py-3 gap-4">
                <span className="text-sm text-white/50 font-poppins flex-shrink-0">{t('profile.location')}</span>
                {loc ? (
                  <span className="text-sm text-white font-poppins text-right">{loc}</span>
                ) : isOwnProfile && onOpenWizard ? (
                  <button
                    onClick={() => onOpenWizard('location')}
                    className="text-sm text-sossiety-red font-poppins font-semibold text-right active:opacity-70"
                  >
                    {t('profile.add_your_location')}
                  </button>
                ) : (
                  <span className="text-sm text-white/40 font-poppins text-right">—</span>
                )}
              </div>
              <div className="flex items-center justify-between py-3 gap-4">
                <span className="text-sm text-white/50 font-poppins flex-shrink-0">{t('profile.profession')}</span>
                {profession ? (
                  <span className="text-sm text-white font-poppins text-right">{profession}</span>
                ) : isOwnProfile && onOpenWizard ? (
                  <button
                    onClick={() => onOpenWizard('profession')}
                    className="text-sm text-sossiety-red font-poppins font-semibold text-right active:opacity-70"
                  >
                    {t('profile.add_your_profession')}
                  </button>
                ) : (
                  <span className="text-sm text-white/40 font-poppins text-right">—</span>
                )}
              </div>
            </div>

            {/* Social Medias */}
            <div className="border-t border-white/10 pt-5 mb-5">
            {(() => {
              const links = [
                social.instagram && { label: 'Instagram', href: getSocialLink('instagram', social.instagram)! },
                social.x        && { label: 'X',         href: getSocialLink('x', social.x)! },
                social.facebook && { label: 'Facebook',  href: getSocialLink('facebook', social.facebook)! },
                social.youtube  && { label: 'YouTube',   href: getSocialLink('youtube', social.youtube)! },
                website         && { label: 'Website',   href: website },
                linkedin        && { label: 'LinkedIn',  href: linkedin },
              ].filter(Boolean) as { label: string; href: string }[];

              if (links.length === 0 && !isOwnProfile) return null;

              return (
                <div>
                  <h3 className="text-sm font-semibold text-white/60 mb-2 font-poppins">
                    {t('profile.social_media')}
                  </h3>
                  {links.length > 0 ? (
                    <p className="flex flex-wrap items-center gap-x-1 gap-y-1">
                      {links.map((link, i) => (
                        <React.Fragment key={link.label}>
                          {i > 0 && <span className="text-white/30 select-none">•</span>}
                          <a
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-white hover:text-white/70 underline font-poppins flex items-center gap-1"
                          >
                            {link.label}
                            <ExternalLink className="w-3 h-3 opacity-50" />
                          </a>
                        </React.Fragment>
                      ))}
                    </p>
                  ) : isOwnProfile && onOpenWizard ? (
                    <button
                      onClick={() => onOpenWizard('social')}
                      className="text-sm text-sossiety-red font-poppins font-semibold active:opacity-70"
                    >
                      {t('profile.add_your_social_links')}
                    </button>
                  ) : null}
                </div>
              );
            })()}
            </div>

            {/* Interests */}
            {(interests.length > 0 || (isOwnProfile && onOpenWizard)) && (
              <div className="mb-5">
                <h3 className="text-sm font-semibold text-white/60 mb-2 font-poppins">
                  {t('profile.interests')}
                </h3>
                {interests.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {interests.map((interest) => (
                      <span key={interest} className="px-3 py-1 bg-sossiety-overlay text-white rounded-full text-[10px] border border-white/30 font-poppins font-medium">
                        {interest}
                      </span>
                    ))}
                  </div>
                ) : isOwnProfile && onOpenWizard ? (
                  <button
                    onClick={() => onOpenWizard('interests')}
                    className="text-sm text-sossiety-red font-poppins font-semibold active:opacity-70"
                  >
                    {t('profile.add_your_interests')}
                  </button>
                ) : null}
              </div>
            )}

            {/* Favorite Techniques */}
            <div className="mb-5">
              <h3 className="text-sm font-semibold text-white/60 mb-3 font-poppins">
                {t('profile.favorite_techniques')}
              </h3>
              {hasAnyTechniques ? (
                <>
                  {/* Category sub-tabs */}
                  <div className="flex overflow-x-auto gap-0 border-b border-white/10 mb-3 scrollbar-none">
                    {techCategoriesWithData.map((category) => (
                      <button
                        key={category}
                        onClick={() => setActiveTechCategory(category)}
                        className={`flex-shrink-0 pb-2 px-3 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors ${
                          resolvedTechCategory === category
                            ? 'text-white border-sossiety-red'
                            : 'text-white/40 border-transparent hover:text-white/70'
                        }`}
                      >
                        {getCategoryDisplayName(category)}
                      </button>
                    ))}
                  </div>
                  {/* Technique pills */}
                  <div className="flex flex-wrap gap-2">
                    {(favoriteTechniques[resolvedTechCategory] || []).map((technique) => (
                      <span key={technique} className="px-3 py-1 bg-sossiety-overlay text-white rounded-full text-[10px] border border-white/30 font-poppins font-medium">
                        {getTechniqueDisplayName(technique)}
                      </span>
                    ))}
                  </div>
                </>
              ) : isOwnProfile && onOpenWizard ? (
                <div className="bg-[#231a1a] border border-dashed border-white/10 rounded-[14px] p-4 flex flex-col gap-2">
                  <p className="text-[13px] font-semibold text-white leading-tight">
                    {t('discovery.cta.add_techniques_title')}
                  </p>
                  <p className="text-[11px] text-white/50 leading-snug">
                    {t('discovery.cta.add_techniques_desc')}
                  </p>
                  <button
                    onClick={() => onOpenWizard('techniques')}
                    className="self-start mt-1 px-4 py-1.5 rounded-full bg-sossiety-red text-white text-[11px] font-semibold active:opacity-80"
                  >
                    {t('discovery.cta.add_techniques')}
                  </button>
                </div>
              ) : (
                <span className="text-sm text-white/60">
                  {t('profile.no_favorite_techniques_added')}
                </span>
              )}
            </div>
          </div>
        )}
        {activeTab === 'social' && (
          <div>
            <MyJourneyTab 
              userId={userId || ''} 
              isOwnProfile={isOwnProfile} 
            />
          </div>
        )}
        {activeTab === 'instructors' && (
          <div className="w-full">
            {/* Instructors content */}
            <div className="mb-6 w-full">
              <InstructorsSection 
                profileId={userId || ''} 
                isOwnProfile={isOwnProfile} 
              />
            </div>
          </div>
        )}
      </div>

      <AuthPrompt
        isOpen={showAuthPrompt}
        onClose={() => setShowAuthPrompt(false)}
        message={authPromptMessage}
      />
    </>
  );
};

export default ProfileHighlights; 